// src/context/PlanContext.js
import React, {
  createContext, useContext, useState, useEffect, useCallback,
} from 'react';
import {
  doc, onSnapshot, setDoc, updateDoc,
  increment,
} from 'firebase/firestore';
import {db} from '../config/firebase';
import {useAuth} from '../Auth/AuthContext';

// ── Límites por plan ───────────────────────────────────────────────────────
export const PLAN_LIMITS = {
  basic: {
    messagesPerMonth: 10,   // mensajes en el único chat
    maxChats: 1,            // solo 1 chat simultáneo
    importTextPerMonth: 5,
    importPhotoPerMonth: 2,
    audioTrials: 1,         // lifetime (no se resetea)
    maxRecipePhotos: 1,
    canUploadVideo: false,
  },
  premium: {
    messagesPerMonth: Infinity,
    maxChats: Infinity,
    importTextPerMonth: Infinity,
    importPhotoPerMonth: Infinity,
    audioTrials: Infinity,
    maxRecipePhotos: Infinity,
    canUploadVideo: true,
  },
};

// Etiquetas legibles para cada feature (usadas en UpgradeModal)
export const FEATURE_LABELS = {
  chat: 'Mensajes al Chef IA',
  newChat: 'Crear chats ilimitados',
  importText: 'Importar receta desde texto',
  importPhoto: 'Importar receta desde foto',
  audio: 'Grabación de voz',
  video: 'Subir vídeos',
  aiRecipeChat: 'Chef IA en recetas',
  photo: 'Múltiples fotos por receta',
};

const PlanContext = createContext(null);
export const usePlan = () => useContext(PlanContext);

const getCurrentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

// ── PlanProvider ───────────────────────────────────────────────────────────
export const PlanProvider = ({children}) => {
  const {userId} = useAuth();

  const [plan, setPlan] = useState('basic');
  const [planExpiresAt, setPlanExpiresAt] = useState(null);
  const [usage, setUsage] = useState({});
  const [audioTrialUsed, setAudioTrialUsed] = useState(false);
  const [upgradeVisible, setUpgradeVisible] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState(null);

  // Escucha el plan del usuario en tiempo real
  useEffect(() => {
    if (!userId) return;
    const unsub = onSnapshot(doc(db, 'users', userId), (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setPlan(data.plan || 'basic');
        setPlanExpiresAt(data.planExpiresAt || null);
        setAudioTrialUsed(data.audioTrialUsed || false);
      }
    });
    return unsub;
  }, [userId]);

  // Escucha el uso mensual en tiempo real
  useEffect(() => {
    if (!userId) return;
    const month = getCurrentMonth();
    const unsub = onSnapshot(
      doc(db, 'users', userId, 'usage', month),
      (snap) => setUsage(snap.exists() ? snap.data() : {}),
    );
    return unsub;
  }, [userId]);

  const isPremium = plan === 'premium' &&
    (!planExpiresAt || planExpiresAt.toDate() > new Date());

  const limits = PLAN_LIMITS[isPremium ? 'premium' : 'basic'];

  // Comprueba si el usuario puede usar una feature
  const canUseFeature = useCallback((feature) => {
    if (isPremium) return true;
    switch (feature) {
      case 'chat':
        return (usage.messageCount || 0) < limits.messagesPerMonth;
      case 'importText':
        return (usage.importTextCount || 0) < limits.importTextPerMonth;
      case 'importPhoto':
        return (usage.importPhotoCount || 0) < limits.importPhotoPerMonth;
      case 'audio':
        return !audioTrialUsed;
      case 'video':
        return false;
      default:
        return true;
    }
  }, [isPremium, usage, limits, audioTrialUsed]);

  // Incrementa el contador de uso para una feature
  const incrementUsage = useCallback(async (feature) => {
    if (!userId || isPremium) return;

    if (feature === 'audio') {
      // El trial de audio es único (lifetime), no mensual
      await updateDoc(doc(db, 'users', userId), {audioTrialUsed: true});
      return;
    }

    const fieldMap = {
      chat: 'messageCount',
      importText: 'importTextCount',
      importPhoto: 'importPhotoCount',
    };
    const field = fieldMap[feature];
    if (!field) return;

    const month = getCurrentMonth();
    await setDoc(
      doc(db, 'users', userId, 'usage', month),
      {[field]: increment(1)},
      {merge: true},
    );
  }, [userId, isPremium]);

  const showUpgradeModal = useCallback((feature = null) => {
    setUpgradeFeature(feature);
    setUpgradeVisible(true);
  }, []);

  const hideUpgradeModal = useCallback(() => {
    setUpgradeVisible(false);
    setUpgradeFeature(null);
  }, []);

  return (
    <PlanContext.Provider value={{
      plan,
      isPremium,
      usage,
      audioTrialUsed,
      limits,
      canUseFeature,
      incrementUsage,
      showUpgradeModal,
      hideUpgradeModal,
      upgradeVisible,
      upgradeFeature,
    }}>
      {children}
    </PlanContext.Provider>
  );
};
